/**
 * O APLICATIVO DE COMPUTADOR "PORTAL SIMULADOS", visto da web (etapa 3, 16/10/2026).
 *
 * O portal sabe que está dentro do aplicativo pela PONTE que o aplicativo expõe (`window.portalSimulados`, em
 * `desktop/src/preload.js`) — e só nas páginas do próprio portal. Não pelo identificador do navegador: aquele texto
 * qualquer navegador pode imitar; a ponte, não. Fora do aplicativo `ponteDoAplicativo()` é nula, e "Aplicar na sala sem
 * internet" não aparece.
 */
import { fraseDaFalhaDoPreparo } from './falhaDoPreparo';
import { api } from '@/utils/api';
import { salasAdotaveis, salasAEncerrarNoPortal } from './salasParaOPortal';
import { encerrarNoPortal } from './encerrarNoPortal';

export interface SalaPreparada {
  id: number;
  simuladoId: number;
  titulo: string;
  questoes: number;
  alunos: number;
  preparadaEm: string;
  reaproveitada: boolean;
  /** 2.0.17 (e o motor do celular, app 1.5): as questões e a duração do pacote, e o estado da sala NESTE aparelho. */
  questoesIds?: number[];
  duracaoSegundos?: number | null;
  estado?: { iniciadaEm?: number | null; encerradaEm?: number | null; envio?: { situacao?: string; mensagem?: string | null } | null; ilegivel?: boolean } | null;
  abertaAgora?: boolean;
  /**
   * 2.0.20 / motor 1.6 (02/11/2026): a versão do pacote e quando foi preparada; a preparação automática e a hora em que a
   * sala pode abrir; o que falta enviar; e a sala que já teve a prova apagada pela limpeza (só as respostas ficam).
   */
  versao?: string | null;
  versaoEm?: string | null;
  inicio?: string | null;
  preparadaSozinha?: boolean;
  abreAPartirDe?: string | null;
  aEnviar?: { entregas: number; fotos: number; presenca: boolean; ilegivel: boolean } | null;
  pacoteApagadoEm?: string | null;
  /** 2.0.21 / motor 1.6: de quem é a sala — só a conta dela a abre neste aparelho. */
  professorId?: number | null;
  professorNome?: string | null;
}

/** Este aparelho (2.0.20 / motor 1.6): o identificador (uma sala por aparelho) e a marca "aplica salas sem internet". */
export interface AparelhoDaSala { id: string; aplicaSalas: boolean; decidido?: boolean; iniciarComOSistema?: boolean; bandeja?: boolean }

export interface PonteDoAplicativo {
  aplicativo: string;
  versao: string;
  plataforma: string;
  aplicarNaSala: (dados: unknown) => Promise<{ ok: true; sala: SalaPreparada } | { ok: false; erro: string }>;
  salasPreparadas: () => Promise<SalaPreparada[]>;
  /** O Wi-Fi e os endereços deste computador na rede (aplicativo 2.0.3 em diante). */
  redeLocal?: () => Promise<{ wifi: string | null; enderecos: string[]; motivo?: string | null }>;
  /** Abre a janela "Sala sem internet" já nesta sala (aplicativo 2.0.4 em diante, etapa 4). */
  abrirSala?: (id: number) => Promise<boolean>;
  /** As salas com resultado guardado cuja credencial venceu, e a entrega da credencial nova (2.0.5, etapa 5). */
  salasSemCredencial?: () => Promise<{ id: number; titulo: string }[]>;
  renovarCredencialDaSala?: (id: number, credencialDaSala: unknown) => Promise<{ ok: boolean; erro?: string; envio?: { situacao: string } | null }>;
  /** 2.0.12 (Plano B, etapa b): a notificação do sistema e o número no ícone — `features/notificacoes/NoAplicativo.tsx`. */
  notificar?: (aviso: { titulo: string; corpo: string }) => Promise<boolean>;
  contador?: (n: number) => Promise<boolean>;
  /** 2.0.20: este aparelho, a chave "aplica salas sem internet", apagar a sala e salvar o arquivo; no Windows, a bandeja. */
  aparelho?: () => Promise<AparelhoDaSala | null>;
  definirAplicaSalas?: (valor: boolean) => Promise<AparelhoDaSala | null>;
  definirIniciarComOSistema?: (valor: boolean) => Promise<AparelhoDaSala | null>;
  apagarSala?: (id: number, opcoes?: { mesmoComPendentes?: boolean }) => Promise<{ ok: boolean; erro?: string; codigo?: string | null }>;
  salvarArquivoDaSala?: (id: number) => Promise<{ ok: boolean; salvo?: boolean; caminho?: string; erro?: string }>;
  /** 2.0.21: a conta da sala neste computador (até o "Sair") e o "Sair" de propósito (`contaDaSala.ts`). */
  contaDaSala?: (conta: { id: number; nome: string }) => Promise<unknown>;
  sairDaConta?: (id: number | null) => Promise<unknown>;
}

/** Quem é o aplicativo, para o portal: a versão decide o que o "um toque" devolve (2.0.5+: credencial só da sala). */
function cabecalhosDoAplicativo(ponte: Partial<PonteDoAplicativo>) {
  return { 'X-Portal-Simulados-Versao': ponte.versao ?? '', 'X-Portal-Simulados-Plataforma': ponte.plataforma ?? '' };
}

declare global {
  interface Window { portalSimulados?: Partial<PonteDoAplicativo> }
}

/** A ponte, quando a página está no aplicativo que já sabe aplicar na sala (2.0.2 em diante); senão, nula. */
export function ponteDoAplicativo(): PonteDoAplicativo | null {
  const p = typeof window !== 'undefined' ? window.portalSimulados : undefined;
  return p && typeof p.aplicarNaSala === 'function' ? (p as PonteDoAplicativo) : null;
}

/**
 * "Aplicar na sala sem internet": o portal cria ou reaproveita a sala do aplicativo e devolve a prova e a credencial
 * (POST /api/offline-rooms/aplicativo); o aplicativo confere e guarda no computador. Lança com a mensagem para a tela.
 */
export async function aplicarNaSalaSemInternet(simulationId: number, turmas?: number[], opcoes: { automatica?: boolean } = {}): Promise<SalaPreparada> {
  const ponte = ponteDoAplicativo();
  if (!ponte) throw new Error('Esta função só funciona no aplicativo Portal Simulados para computador.');
  // UMA SALA POR APARELHO (2.0.20): o identificador deste computador e as salas que ele já guarda vão no pedido.
  const aparelho = ponte.aparelho ? await ponte.aparelho().catch(() => null) : null;
  // As que o portal pode adotar, e as deste simulado já encerradas e enviadas encerradas lá antes (`salasParaOPortal.ts`).
  const guardadas = aparelho ? await ponte.salasPreparadas().catch(() => []) : [];
  const salasNoAparelho = salasAdotaveis(guardadas);
  if (aparelho) await encerrarNoPortal(salasAEncerrarNoPortal(guardadas, simulationId));
  let dados: { sala: unknown; pacote: unknown };
  try {
    dados = (await api.post('/api/offline-rooms/aplicativo', { simulationId, ...(turmas?.length ? { turmas } : {}), ...(salasNoAparelho.length ? { salasNoAparelho } : {}), ...(opcoes.automatica ? { automatica: true } : {}) }, {
      headers: { ...cabecalhosDoAplicativo(ponte), ...(aparelho ? { 'X-Portal-Simulados-Aparelho': aparelho.id } : {}) },
    })).data;
  } catch (e) {
    // A causa real (03/11/2026): recusa por regra, sessão, servidor ou rede — "confira a internet" só quando é a rede.
    throw new Error(fraseDaFalhaDoPreparo(e));
  }
  const r = await ponte.aplicarNaSala({ ...dados, portal: window.location.origin, ...(opcoes.automatica ? { automatica: true } : {}) });
  if (!r.ok) throw new Error(r.erro);
  return r.sala;
}

/**
 * A CREDENCIAL DA SALA VENCEU (etapa 5, achado 271): os resultados ficaram guardados no computador, e o aplicativo
 * pede ao professor que entre no portal pela janela dele. Entrou: para cada sala nessa situação, o portal emite uma
 * credencial nova (com a sessão do professor, o dono da sala) e a página a entrega ao aplicativo, que envia em seguida.
 * Devolve as salas renovadas e as que falharam, para a tela avisar.
 */
export async function renovarCredenciaisDasSalas(): Promise<{ renovadas: string[]; falhas: string[] }> {
  const ponte = typeof window !== 'undefined' ? window.portalSimulados : undefined;
  if (!ponte?.salasSemCredencial || !ponte.renovarCredencialDaSala) return { renovadas: [], falhas: [] };
  const salas = await ponte.salasSemCredencial();
  const renovadas: string[] = [];
  const falhas: string[] = [];
  for (const s of salas) {
    try {
      const { data } = await api.post(`/api/offline-rooms/aplicativo/${s.id}/credencial`, {}, { headers: cabecalhosDoAplicativo(ponte) });
      const r = await ponte.renovarCredencialDaSala(s.id, data.credencialDaSala);
      (r.ok ? renovadas : falhas).push(s.titulo);
    } catch {
      falhas.push(s.titulo);
    }
  }
  return { renovadas, falhas };
}
